import { Capacitor } from "@capacitor/core";

import { LocalNotifications, LocalNotificationSchema } from "@capacitor/local-notifications";
import {
  AppUser,
  buildItemPath,
  buildItemSchemeLink,
  ICategory,
  IItinerarymap,
  ItineraryFilter,
  spLogTags,
  UpNextTimelineEntrySource,
} from "@eventr/common";
import _ from "lodash";
import i18n from "../../i18n/i18next";
import AppGroupUserDefaults from "../../plugins/appGroupUserDefaults/AppGroupUserDefaultsPlugin";
import { bs, useBoundStore } from "../../state/bound.store";
import { toItineraryFilterOptions } from "../../state/filterTargetsState/filterTargets.adapter";
import { FilterTarget, FilterTargetsByItinerary } from "../../state/filterTargetsState/filterTargets.interface";
import { resolveFilterSelection } from "../../state/filterTargetsState/filterTargets.slice";
import { IItinerary } from "../../state/interfaces";
import { replaceRenderOptions } from "../../state/itineraryState/itineraryMap.slice";
import { useUserPrefsStore } from "../../state/userPrefsState/userPrefs.store";

type NotifEntry = UpNextTimelineEntrySource & {
  itineraryId: number;
  itineraryName?: string; // name of the itinerary this entry belongs to (surfaced in the iOS widget)
  PIT?: boolean; // indicates if the entry is a PIT entry
};

export function entryGenerator(selectedData: {
  itineraryData: {
    [key: string]: IItinerary;
  };
  scheduleLocalNotifications: boolean;
  notificationPermissionsGranted: boolean;
  filterTargets: FilterTargetsByItinerary;
  itineraryMap: IItinerarymap[];
}) {
  const { itineraryData, scheduleLocalNotifications, notificationPermissionsGranted, filterTargets, itineraryMap } =
    selectedData;
  const wpUser = bs().wpUser;

  if (!wpUser) {
    spLogTags("User logged out, cannot sync notifications or widget", ["ERROR", "localNotifications", "sync"]);
    return;
  }

  // Visibility matching needs the full org-directory user (its department/group tags); the raw
  // `wpUser` ("me") can be missing those, so resolve it by id via getCurrentUser (falling back to wpUser).
  const user = bs().getCurrentUser() ?? wpUser;

  // Custom categories are resolved per entry by ItineraryFilter; they rarely change, so read them once here.
  const orgCategories = bs().orgConfig?.categories ?? [];

  // Local notifications honour the "notifications" target selection.
  if (scheduleLocalNotifications && notificationPermissionsGranted && Capacitor.getPlatform() !== "web") {
    const notificationEntries = getAllEntries(
      "notifications",
      filterTargets,
      itineraryData,
      itineraryMap,
      user,
      orgCategories,
    );
    spLogTags(`${notificationEntries.length} entries parsed`, ["parsing", "localNotifications"]);
    scheduleNotifications(notificationEntries, itineraryMap);
  } else {
    syncLocalNotifications([]);
  }

  // iOS widgets honour their own independent "widgets" target selection.
  if (Capacitor.getPlatform() === "ios") {
    const widgetEntries = getAllEntries("widgets", filterTargets, itineraryData, itineraryMap, user, orgCategories);
    syncWidget(widgetEntries);
  }
}

function syncWidget(entries: (UpNextTimelineEntrySource & { itineraryId: number })[]) {
  spLogTags("SYNCING WIDGET", ["sync", "widget"]);
  const subset = sortEntries(entries).slice(0, 20);

  spLogTags(bs().timelineEntries, ["sync", "widget"]);
  if (Capacitor.getPlatform() === "ios" && !_.isEqual(bs().timelineEntries, subset)) {
    spLogTags(JSON.stringify(subset), ["sync", "widget"]);

    AppGroupUserDefaults.set({
      key: "itinerary",
      // Attach a per-item deeplink the widget uses for its tap target (widgetURL).
      value: JSON.stringify(
        subset.map((entry) => ({
          ...entry,
          deeplink:
            entry.section && entry.id
              ? buildItemSchemeLink(entry.section, entry.id, bs().selectedOrg?.name)
              : undefined,
        })),
      ),
      reloadAllTimelines: true,
    });

    // Update cached payload in the store
    useBoundStore.setState({
      timelineEntries: subset,
    });
  }
}

/** Orders timeline entries soonest-first by their event time. */
function sortEntries(
  entries: (UpNextTimelineEntrySource & { itineraryId: number })[],
): (UpNextTimelineEntrySource & { itineraryId: number })[] {
  return [...entries].sort((a, b) => (a?.date?.timestamp ?? 0) - (b?.date?.timestamp ?? 0));
}

/** iOS keeps at most 64 pending local notifications; leave headroom and spread coverage under it. */
const IOS_PENDING_CAP = 60;

/**
 * Chooses which notifications to schedule when there are more than iOS will keep. Each itinerary
 * gets its soonest few first (so no trip is starved when another has many imminent events), then
 * the remaining slots are filled with the soonest overall.
 */
function prioritiseForIosCap<T extends { timestamp: number; id: string }>(notifications: T[]): T[] {
  if (notifications.length <= IOS_PENDING_CAP) return notifications;

  const sorted = [...notifications].sort((a, b) => a.timestamp - b.timestamp);
  const itineraryOf = (notification: T) => notification.id.split("_")[0] ?? "";
  const itineraryCount = new Set(sorted.map(itineraryOf)).size;
  const softCapPerItinerary = Math.max(1, Math.floor(IOS_PENDING_CAP / Math.max(1, itineraryCount)));

  const perItinerary: Record<string, number> = {};
  const chosen: T[] = [];
  const deferred: T[] = [];

  for (const notification of sorted) {
    if (chosen.length >= IOS_PENDING_CAP) break;
    const itinerary = itineraryOf(notification);
    if ((perItinerary[itinerary] ?? 0) < softCapPerItinerary) {
      chosen.push(notification);
      perItinerary[itinerary] = (perItinerary[itinerary] ?? 0) + 1;
    } else {
      deferred.push(notification);
    }
  }
  for (const notification of deferred) {
    if (chosen.length >= IOS_PENDING_CAP) break;
    chosen.push(notification);
  }
  return chosen;
}

function scheduleNotifications(entries: NotifEntry[], itineraryMap: IItinerarymap[]) {
  const notifications: {
    title: string;
    body: string;
    timestamp: number;
    id: string;
    path?: string;
  }[] = [];
  sortEntries(entries)
    .slice(0, 500)
    .forEach((entry: NotifEntry) => {
      const title = itineraryMap.find(
        (itineraryMapElement) => itineraryMapElement.id === entry.itineraryId,
      )?.itinerary_name;

      if (!title) return;

      // Deeplink path to the item this notification is about; tapping it opens the item page.
      const path =
        entry?.section && entry?.id ? buildItemPath(entry.section, entry.id, bs().selectedOrg?.name) : undefined;

      if (entry?.PIT) {
        if (entry?.section && entry?.date && entry?.date?.timestamp) {
          notifications.push({
            title,
            body: entry?.primary ?? "",
            timestamp: entry?.date?.timestamp,
            id: `${entry.itineraryId}_${entry?.section}_${entry?.id}_${entry?.startOrEnd ?? ""}`,
            path,
          });
        }
        const arrivalNotif = setArrivalTime(entry);
        if (arrivalNotif) {
          notifications.push({
            ...arrivalNotif,
            title,
            path,
          });
        }
      } else {
        if (entry?.section && entry?.date && entry?.date?.timestamp) {
          notifications.push({
            title,
            body: getLabel(entry?.section, entry.startOrEnd) + " " + entry?.primary,
            timestamp: entry?.date?.timestamp,
            id: `${entry.itineraryId}_${entry?.section}_${entry?.id}_${entry?.startOrEnd ?? ""}`,
            path,
          });
          if (entry.startOrEnd === "start") {
            const arrivalNotif = setArrivalTime(entry);
            if (arrivalNotif) {
              notifications.push({
                ...arrivalNotif,
                title,
                path,
              });
            }
          }
        }
      }
    });

  const localNotifications: LocalNotificationSchema[] = prioritiseForIosCap(notifications).map((notification, index) => {
    return {
      id: index,
      title: notification.title,
      body: notification.body,
      schedule: {
        at: new Date(notification.timestamp),
      },
      extra: { id: notification.id, path: notification.path },
    };
  });

  // Cancel all notifications before scheduling new ones
  syncLocalNotifications(localNotifications);
}

function getLabel(
  section: "flight" | "accommodation" | "transport" | "general-activity" | "parking" | "hire-car",
  startOrEnd?: "start" | "end",
): string {
  switch (section) {
    case "flight":
    case "transport":
      return startOrEnd === "start" ? "Departs:" : "Arrives:";

    case "accommodation":
      return startOrEnd === "start" ? "Check-in:" : "Check-out:";

    case "general-activity":
      return startOrEnd === "start" ? "Starts:" : "Ends:";

    case "parking":
      return startOrEnd === "start" ? "Entry:" : "Exit:";

    case "hire-car":
      return startOrEnd === "start" ? "Pick-up:" : "Drop-off:";
  }
}

/**
 * Builds the timeline entries for a target (notifications or widgets) across every downloaded,
 * still-running itinerary. `ItineraryFilter.toTimelineSources` is the single engine: it applies the
 * user's selection and fans each surviving entry into its start/end widget rows.
 */
function getAllEntries(
  target: FilterTarget,
  filterTargets: FilterTargetsByItinerary,
  itineraryData: { [key: string]: IItinerary },
  itineraryMap: IItinerarymap[],
  user: AppUser,
  orgCategories: ICategory[],
): NotifEntry[] {
  const zoneType = useUserPrefsStore.getState().showDestTime;

  return itineraryMap
    .filter((itineraryMapElement) => {
      const isDownloaded = Object.keys(itineraryData).includes(itineraryMapElement.id.toString());
      const isInTheFuture = itineraryMapElement.end_time * 1000 >= new Date().getTime();

      return isDownloaded && isInTheFuture;
    })
    .flatMap((itineraryMapElement): NotifEntry[] => {
      const selection = resolveFilterSelection(filterTargets, itineraryMapElement.id, target);

      // Itinerary switched off for this target → it contributes nothing.
      if (!selection.enabled) return [];

      const itineraryRecord = itineraryData[itineraryMapElement.id];
      if (!itineraryRecord) return [];

      const itinerary = replaceRenderOptions(itineraryRecord);

      const sources = new ItineraryFilter(
        itinerary.v2 ?? [],
        toItineraryFilterOptions(selection, target, orgCategories, user),
      ).toTimelineSources({ userId: user.id, zoneType, maxEntries: 1000 });

      return sources.map((source) => ({
        ...source,
        itineraryId: itineraryMapElement.id,
        itineraryName: itineraryMapElement.itinerary_name,
      })) as NotifEntry[];
    });
}

function setArrivalTime(entry: NotifEntry) {
  if (entry.checkIn?.timestamp) {
    if (entry.checkIn?.timestamp !== entry?.date?.timestamp) {
      return {
        body: `${i18n.t("cards.checkIn")}: ${entry?.primary}`,
        timestamp: entry?.checkIn?.timestamp,
        id: `${entry.itineraryId}_${entry?.section}_${entry?.id}_checkin_${entry?.startOrEnd ?? ""}`,
      };
    }
  } else {
    const sourceTs = entry?.date?.timestamp;
    const generatedTs = sourceTs ? sourceTs - 15 * 60 * 1000 : 0;
    return {
      body: `${i18n.t("cards.checkIn")}: ${entry?.primary}`,
      timestamp: generatedTs,
      id: `${entry.itineraryId}_${entry?.section}_${entry?.id}_checkin_${entry?.startOrEnd ?? ""}`,
    };
  }
}

function syncLocalNotifications(localNotifications: any[]) {
  LocalNotifications.getPending().then((res) => {
    const pending = res.notifications ?? []; //already scheduled

    const now = Date.now();

    // No incoming notifications → cancel everything
    if (localNotifications.length === 0) {
      if (pending.length > 0) {
        LocalNotifications.cancel({
          notifications: pending.map((notification) => ({
            id: notification.id,
          })),
        });
      }
      return;
    }

    const incomingNotiesIds = new Set(localNotifications.map((notification) => notification.extra?.id));
    // Existing pending notifications mapped by their extra.id
    const pendingByExtraId = new Map(pending.map((notification) => [notification.extra?.id, notification]));

    // Cancel pending notifications not in incoming list
    const toCancel = pending
      .filter((notification) => !incomingNotiesIds.has(notification.extra?.id))
      .map((notification) => ({ id: notification.id }));

    if (toCancel.length > 0) {
      LocalNotifications.cancel({ notifications: toCancel });
    }

    //  Sync incoming notifications
    localNotifications.forEach((notification) => {
      const scheduledTime = notification.schedule?.at?.valueOf() ?? 0;

      if (scheduledTime < now) return;

      const existing = pendingByExtraId.get(notification.extra?.id); //check if incoming already exists in pending

      // Not scheduled yet → schedule
      if (!existing) {
        LocalNotifications.schedule({
          notifications: [notification],
        });
        return;
      }

      // Exists → check for changes
      const hasChanged =
        existing.title !== notification.title ||
        existing.body !== notification.body ||
        existing.schedule?.at?.valueOf() !== scheduledTime;

      // Changed → cancel and reschedule
      if (hasChanged) {
        LocalNotifications.cancel({
          notifications: [{ id: existing.id }],
        }).then(() => {
          LocalNotifications.schedule({
            notifications: [notification],
          });
        });
      }
    });
  });
}
